import type {
  CourseChatAttachment,
  CourseChatMessage,
  CourseChatScope,
} from "services/courseChatService";

/**
 * WhatsApp-style local cache for course chat messages (web).
 *
 * Renders the last messages instantly on reopen while Firestore reconnects in
 * the background.
 *
 * Storage: localStorage (simple, synchronous, per-origin). Signed R2 URLs are
 * NEVER persisted — only safe message metadata is stored.
 */

const CACHE_PREFIX = "chat_messages:";
const UID_PREFIX = "chat_uid:";

/** Keep cache bounded so localStorage stays small. */
const MAX_CACHED_MESSAGES = 100;

/** Drop caches older than this so we never resurrect very stale chats. */
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

type CachedChat = {
  courseId: number;
  firebaseUid: string;
  updatedAt: number;
  lastMessageMillis: number;
  messages: CourseChatMessage[];
};

function cacheKey(courseId: string | number, firebaseUid: string): string {
  return `${CACHE_PREFIX}${courseId}:${firebaseUid}`;
}

function uidKey(scope: CourseChatScope): string {
  return `${UID_PREFIX}${scope}`;
}

/**
 * Remembers the last-known firebase uid for a scope so the next chat open can
 * build the cache key and render instantly before bootstrap completes.
 */
export function rememberFirebaseUid(scope: CourseChatScope, firebaseUid: string): void {
  try {
    if (firebaseUid) localStorage.setItem(uidKey(scope), firebaseUid);
  } catch {
    // ignore
  }
}

export function getRememberedFirebaseUid(scope: CourseChatScope): string | null {
  try {
    return localStorage.getItem(uidKey(scope));
  } catch {
    return null;
  }
}

/** Strips signed URLs + optimistic-only fields; keeps safe message metadata. */
function sanitizeForCache(message: CourseChatMessage): CourseChatMessage {
  const isDeleted = Boolean(message.is_deleted);
  const attachment: CourseChatAttachment | null =
    !isDeleted && message.attachment
      ? {
          url: null,
          path: message.attachment.path ?? null,
          name: message.attachment.name ?? null,
          mime: message.attachment.mime ?? null,
          size: message.attachment.size ?? null,
          duration_seconds: message.attachment.duration_seconds ?? null,
        }
      : null;

  return {
    id: message.id,
    course_id: message.course_id,
    sender: {
      id: message.sender.id,
      name: message.sender.name ?? null,
      role: message.sender.role,
      avatar_url: message.sender.avatar_url ?? null,
    },
    message_type: message.message_type,
    body: isDeleted ? null : message.body,
    attachment,
    client_message_id: message.client_message_id ?? null,
    is_mine: message.is_mine,
    read_count: null,
    created_at: message.created_at ?? null,
    created_at_millis: message.created_at_millis,
    created_at_human: null,
    is_deleted: isDeleted,
  };
}

export function loadCachedMessages(
  courseId: string | number,
  firebaseUid: string | null
): CourseChatMessage[] {
  if (!firebaseUid) return [];
  try {
    const raw = localStorage.getItem(cacheKey(courseId, firebaseUid));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CachedChat;
    if (!parsed || !Array.isArray(parsed.messages)) return [];
    if (parsed.updatedAt && Date.now() - parsed.updatedAt > CACHE_TTL_MS) {
      localStorage.removeItem(cacheKey(courseId, firebaseUid));
      return [];
    }
    return parsed.messages;
  } catch {
    return [];
  }
}

export function saveCachedMessages(
  courseId: string | number,
  firebaseUid: string | null,
  messages: CourseChatMessage[]
): void {
  if (!firebaseUid) return;
  try {
    // Cache only confirmed server messages (drop optimistic/local ones).
    const confirmed = messages.filter((m) => m.local !== true && !m.status);
    const trimmed = confirmed.slice(-MAX_CACHED_MESSAGES).map(sanitizeForCache);
    const lastMessageMillis = trimmed.length
      ? trimmed[trimmed.length - 1].created_at_millis ?? 0
      : 0;

    const payload: CachedChat = {
      courseId: Number(courseId),
      firebaseUid,
      updatedAt: Date.now(),
      lastMessageMillis,
      messages: trimmed,
    };
    localStorage.setItem(cacheKey(courseId, firebaseUid), JSON.stringify(payload));
  } catch {
    // Caching is best-effort (quota / private mode); ignore failures.
  }
}

/** Clears all chat caches (e.g. on logout), optionally scoped to one uid. */
export function clearAllChatCache(firebaseUid?: string): void {
  try {
    const remove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (key.startsWith(CACHE_PREFIX) && (!firebaseUid || key.endsWith(`:${firebaseUid}`))) {
        remove.push(key);
      }
    }
    remove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}
